import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Error boundary around the lazily-loaded kiosk keyboard (TCGMA-176).
 *
 * WHY: the keyboard was mounted as `<Suspense fallback={null}><KioskKeyboard/></Suspense>`
 * with no boundary. That combination swallows a failed dynamic import — and a
 * throw at module-execute time — into complete silence: the flag reads true, the
 * chunk serves 200, and NOTHING appears in the DOM. Admin hit exactly that on the
 * real panel and had no error to work from; from here it was indistinguishable
 * from "the component decided not to render". The suite couldn't see it either,
 * because in a healthy browser the import simply doesn't fail.
 *
 * A kiosk is unattended and has no console in front of it, so a silent dead
 * keyboard means a customer just stands there unable to type. Two jobs:
 *
 *  1. Record the real error at `window.__tcgKioskError` so a CDP trace can read
 *     WHY, instead of us guessing across a network from a description.
 *  2. Show a visible fallback. A keyboard that failed to load should SAY so —
 *     the staff member walking past can see it, and the customer knows the panel
 *     isn't just ignoring them.
 *
 * Rendering nothing on failure is what let this hide for a full deploy cycle.
 */

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class KioskKeyboardBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced for a remote CDP trace — the panel has no visible console.
    try {
      (window as unknown as Record<string, unknown>).__tcgKioskError = {
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
        at: new Date().toISOString(),
      };
    } catch {
      /* never let the reporter be the thing that throws */
    }
    console.error("[kiosk] on-screen keyboard failed to mount", error, info);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div
        data-kiosk-keyboard-error=""
        className="fixed inset-x-0 bottom-0 z-50 bg-card p-3 text-center text-sm"
        style={{ borderTop: "2px solid #c9a84c" }}
      >
        <span style={{ color: "#e8eaf0" }}>
          On-screen keyboard unavailable — please ask a staff member.
        </span>
      </div>
    );
  }
}
